import React, { useState } from 'react';
import { Fab, Tooltip, Chip, Box, CircularProgress } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * 🎭 Impersonation (kullanıcı taklit) sırasında ekranın köşesinde beliren,
 * dikkat çekmeyen "Yöneticiye dön" yüzen butonu.
 *
 * - Sadece aktif bir impersonation oturumunda görünür.
 * - Tam ekran satış/POS ekranlarını bozmaması için sabit, köşede konumlanır.
 * - Tıklanınca süper admin oturumuna geri döner ve /superadmin'e gider.
 */
const ImpersonationFab: React.FC = () => {
  const { isImpersonating, impersonationInfo, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const [returning, setReturning] = useState(false);

  if (!isImpersonating) return null;

  const targetName =
    [impersonationInfo?.targetUser?.firstName, impersonationInfo?.targetUser?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    impersonationInfo?.targetUser?.email ||
    'kullanıcı';

  const handleReturn = async () => {
    setReturning(true);
    try {
      // userDetails (rol) süper admin'e dönene kadar bekle, sonra yönlendir.
      await stopImpersonation();
      navigate('/superadmin');
    } catch (e) {
      console.error('[ImpersonationFab] Yöneticiye dönüş hatası:', e);
    } finally {
      setReturning(false);
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pointerEvents: 'none',
      }}
    >
      <Chip
        icon={<TheaterComedyIcon />}
        label={`${targetName} olarak görüntülüyorsunuz`}
        color="warning"
        size="small"
        sx={{
          pointerEvents: 'auto',
          bgcolor: 'rgba(237, 108, 2, 0.92)',
          color: 'white',
          fontWeight: 600,
          boxShadow: 3,
          '& .MuiChip-icon': { color: 'white' },
          display: { xs: 'none', sm: 'flex' },
        }}
      />
      <Tooltip title="Yöneticiye (süper admin) dön">
        <Fab
          size="small"
          color="warning"
          onClick={handleReturn}
          disabled={returning}
          sx={{ pointerEvents: 'auto', color: 'white' }}
          aria-label="Yöneticiye dön"
        >
          {returning ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <LogoutIcon />}
        </Fab>
      </Tooltip>
    </Box>
  );
};

export default ImpersonationFab;
